import React, { useState, useEffect, createContext, useContext } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Header } from './Header/Header';
import { Version } from './Version/Version';
import LocationPicker from './LocationPicker/LocationPicker';
import { loadLocationPreference, saveLocationPreference } from '../utils/locationPersistence';
import { loadRadiusPreference, saveRadiusPreference } from '../utils/radiusPersistence';
import { buildEventsApiUrl } from '../config/api';
import { getAuthUserId } from '../utils/authUser';
import { RootState } from '../store/store';
import type { Location } from '../services/apiService';

interface LayoutContextType {
  userLocation: { city: string; state: string; lat: number; lng: number } | null;
  searchRadius: number;
  searchQuery: string;
  setUserLocation: (location: { city: string; state: string; lat: number; lng: number }) => void;
  setSearchRadius: (radius: number) => void;
  setSearchQuery: (query: string) => void;
}

const LayoutContext = createContext<LayoutContextType | null>(null);

export function useLayoutContext() {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayoutContext must be used within Layout');
  }
  return context;
}

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [userLocation, setUserLocationState] = useState<{ city: string; state: string; lat: number; lng: number } | null>(null);
  const [searchRadius, setSearchRadiusState] = useState(25);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [eventDayBanner, setEventDayBanner] = useState<any>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, initialized, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const userId = getAuthUserId(user as any);

  // Load preferences on mount
  useEffect(() => {
    loadLocationPreference().then(location => {
      if (location) {
        setUserLocationState(location);
      }
    });
    
    // loadRadiusPreference returns a number directly, not a Promise
    const radius = loadRadiusPreference();
    if (radius) {
      setSearchRadiusState(radius);
    }
  }, []);

  const setUserLocation = (location: { city: string; state: string; lat: number; lng: number }) => {
    setUserLocationState(location);
    saveLocationPreference(location);
    setShowLocationPicker(false);
  };

  const setSearchRadius = (radius: number) => {
    setSearchRadiusState(radius);
    saveRadiusPreference(radius);
  };

  const handleLocationSelect = (location: Location) => {
    setUserLocationState(location);
    saveLocationPreference(location);
    setShowLocationPicker(false);
  };

  useEffect(() => {
    if (!initialized || !isAuthenticated || !userId) {
      setEventDayBanner(null);
      return;
    }

    let cancelled = false;
    const loadBanner = async () => {
      try {
        const response = await fetch(buildEventsApiUrl(`/api/event-day/banner/${encodeURIComponent(userId)}`));
        const data = await response.json();
        if (!response.ok || cancelled) return;
        setEventDayBanner(data.banner || null);
      } catch {
        if (!cancelled) {
          setEventDayBanner(null);
        }
      }
    };

    loadBanner();
    const intervalId = window.setInterval(loadBanner, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [initialized, isAuthenticated, userId]);

  const hideEventDayBanner = Boolean(eventDayBanner?.eventId && location.pathname === `/events/${eventDayBanner.eventId}/check-in`);

  return (
    <LayoutContext.Provider value={{ userLocation, searchRadius, searchQuery, setUserLocation, setSearchRadius, setSearchQuery }}>
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        backgroundColor: '#f8f9fa'
      }}>
        {/* Header */}
        <Header 
          onLocationClick={() => setShowLocationPicker(true)}
          userLocation={userLocation}
          searchRadius={searchRadius}
          onRadiusChange={setSearchRadius}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />

        {!hideEventDayBanner && eventDayBanner && (
          <Box sx={{ px: { xs: 2, md: 3 }, pt: 2 }}>
            <Box sx={{ maxWidth: '1200px', margin: '0 auto' }}>
              <Alert severity={eventDayBanner.severity || 'info'} sx={{ borderRadius: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {eventDayBanner.eventName}
                    </Typography>
                    <Typography variant="body2">
                      {eventDayBanner.message}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => navigate(`/events/${eventDayBanner.eventId}/check-in`)}
                  >
                    Return to Event Check-In
                  </Button>
                </Stack>
              </Alert>
            </Box>
          </Box>
        )}

        {/* Main Content */}
        <Box sx={{ 
          flex: 1,
          maxWidth: '100%'
        }}>
          {children}
        </Box>

        {/* Footer */}
        <Box sx={{
          backgroundColor: 'white',
          borderTop: '1px solid #e0e0e0',
          padding: '24px 20px',
          marginTop: 'auto'
        }}>
          <Box sx={{
            maxWidth: '1200px',
            margin: '0 auto',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box sx={{ 
              fontSize: '14px', 
              color: '#6c757d',
              textAlign: 'left'
            }}>
              © {new Date().getFullYear()} Crowd Trades. All rights reserved.
            </Box>
            <Version />
          </Box>
        </Box>

        {/* Location Picker Modal */}
        <LocationPicker
          open={showLocationPicker}
          onClose={() => setShowLocationPicker(false)}
          onLocationSelect={handleLocationSelect}
        />
      </Box>
    </LayoutContext.Provider>
  );
}
